import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Target,
  Copy,
  Check,
  ExternalLink,
  Play,
  CheckCircle2,
  AlertCircle,
  Clock,
  FolderOpen,
  AlertTriangle,
  Layers,
  BarChart3,
  Database,
  Brain,
  Cpu,
  MemoryStick,
  Plus,
  ArrowRight,
  Sparkles,
} from 'lucide-react';
import {
  TAO6InstanceDetails,
  TAO6Experiment,
  TAO6TaskType,
  mockTAO6InstanceDetails,
} from '@/mock/gpu-instance';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';
import useDemoPath from '@/hook/useDemoPath';
import PATH_PAGE from '@/router/path';

interface TAODetailPanelProps {
  taskId: string;
}

const getTaskIcon = (task: TAO6TaskType) => {
  switch (task) {
    case 'object_detection':
      return <Target className="h-4 w-4 text-green-500" />;
    case 'image_classification':
      return <Layers className="h-4 w-4 text-blue-500" />;
  }
};

const getStatusBadge = (status: TAO6Experiment['status']) => {
  switch (status) {
    case 'training':
      return (
        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30">
          <Play className="h-3 w-3 mr-1" /> Training
        </Badge>
      );
    case 'completed':
      return (
        <Badge className="bg-green-500/10 text-green-600 border-green-500/30">
          <CheckCircle2 className="h-3 w-3 mr-1" /> Completed
        </Badge>
      );
    case 'failed':
      return (
        <Badge className="bg-red-500/10 text-red-600 border-red-500/30">
          <AlertCircle className="h-3 w-3 mr-1" /> Failed
        </Badge>
      );
    case 'queued':
      return (
        <Badge className="bg-gray-500/10 text-gray-600 border-gray-500/30">
          <Clock className="h-3 w-3 mr-1" /> Queued
        </Badge>
      );
    case 'evaluating':
      return (
        <Badge className="bg-purple-500/10 text-purple-600 border-purple-500/30">
          <BarChart3 className="h-3 w-3 mr-1" /> Evaluating
        </Badge>
      );
    case 'exporting':
      return (
        <Badge className="bg-yellow-500/10 text-yellow-600 border-yellow-500/30">
          <Sparkles className="h-3 w-3 mr-1" /> Exporting
        </Badge>
      );
  }
};

const TAODetailPanel = ({ taskId }: TAODetailPanelProps) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const { orgId, workspaceId } = useParamWorkspaceId();
  const { withDemoPrefix } = useDemoPath();
  const navigate = useNavigate();

  const details: TAO6InstanceDetails = useMemo(() => mockTAO6InstanceDetails(taskId), [taskId]);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const activeExperiments = details.experiments.filter((e) => e.status === 'training').length;
  const completedExperiments = details.experiments.filter((e) => e.status === 'completed').length;
  const queuedExperiments = details.experiments.filter((e) => e.status === 'queued').length;

  // Get GPU usage color
  const getGpuUsageColor = (usage: number) => {
    if (usage >= 80) return 'text-green-600';
    if (usage >= 50) return 'text-yellow-600';
    return 'text-gray-600';
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="border-green-500/50 mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-green-500/10 to-emerald-500/10">
                <Target className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <CardTitle className="flex items-center gap-2">
                  NVIDIA TAO Toolkit
                  <Badge
                    variant="outline"
                    className={
                      details.status === 'active'
                        ? 'bg-green-500/10 text-green-600 border-green-500/30'
                        : 'bg-gray-500/10 text-gray-600'
                    }
                  >
                    {details.status}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Transfer learning toolkit for computer vision - TAO 6.x
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="font-mono bg-green-500/10 text-green-600">
                v{details.taoVersion}
              </Badge>
              <Button size="sm" variant="outline" onClick={() => window.open(details.notebookUrl, '_blank')}>
                <ExternalLink className="h-4 w-4 mr-1" />
                Open Jupyter
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* GPU Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Cpu className="h-4 w-4" />
                GPU Utilization
              </div>
              <span className={`font-mono font-bold ${getGpuUsageColor(details.gpuUtilization)}`}>
                {details.gpuUtilization}%
              </span>
            </div>
            <Progress value={details.gpuUtilization} className="h-2" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <MemoryStick className="h-4 w-4" />
                GPU Memory
              </div>
              <span className="font-mono font-medium">
                {details.gpuMemoryUsed}/{details.gpuMemoryTotal} GB
              </span>
            </div>
            <Progress
              value={(details.gpuMemoryUsed / details.gpuMemoryTotal) * 100}
              className="h-2"
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 text-center">
            <div className="text-3xl font-bold">{details.experiments.length}</div>
            <div className="text-sm text-muted-foreground">Total Experiments</div>
          </CardContent>
        </Card>
      </div>

      {/* Experiment Stats Grid */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:border-primary/50 transition-all">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{activeExperiments}</div>
            <div className="text-sm text-muted-foreground">Training</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary/50 transition-all">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-green-600">{completedExperiments}</div>
            <div className="text-sm text-muted-foreground">Completed</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary/50 transition-all">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold text-gray-600">{queuedExperiments}</div>
            <div className="text-sm text-muted-foreground">Queued</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-primary/50 transition-all">
          <CardContent className="pt-4 text-center">
            <div className="text-2xl font-bold">{details.datasets.length}</div>
            <div className="text-sm text-muted-foreground">Datasets</div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-3 gap-4">
        <Card
          className="cursor-pointer hover:border-primary/50 transition-all"
          onClick={() =>
            navigate(
              withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, taskId).root + '/tao-experiments')
            )
          }
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <BarChart3 className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <div className="font-medium">Experiments</div>
                  <div className="text-sm text-muted-foreground">
                    Manage training experiments
                  </div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:border-primary/50 transition-all"
          onClick={() =>
            navigate(
              withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, taskId).root + '/tao-datasets')
            )
          }
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10">
                  <Database className="h-5 w-5 text-purple-500" />
                </div>
                <div>
                  <div className="font-medium">Datasets</div>
                  <div className="text-sm text-muted-foreground">
                    Browse sample datasets
                  </div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card
          className="cursor-pointer hover:border-primary/50 transition-all"
          onClick={() =>
            navigate(
              withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, taskId).root + '/tao-models')
            )
          }
        >
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-500/10">
                  <Brain className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <div className="font-medium">Model Catalog</div>
                  <div className="text-sm text-muted-foreground">
                    NGC pretrained models
                  </div>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Experiments */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Recent Experiments</CardTitle>
              <CardDescription>Latest training experiments</CardDescription>
            </div>
            <Button
              size="sm"
              onClick={() =>
                navigate(
                  withDemoPrefix(PATH_PAGE.dashboard.gpuInstance.byId(orgId, workspaceId, taskId).root + '/tao-experiments')
                )
              }
            >
              <Plus className="h-4 w-4 mr-1" />
              New Experiment
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {details.experiments.slice(0, 3).map((experiment) => (
            <Card key={experiment.id} className="bg-muted/30">
              <CardContent className="pt-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    {getTaskIcon(experiment.task)}
                    <div>
                      <div className="font-medium">{experiment.name}</div>
                      <div className="text-sm text-muted-foreground">
                        Model: {experiment.pretrainedModel.displayName} | Dataset:{' '}
                        {experiment.dataset.name}
                      </div>
                    </div>
                  </div>
                  {getStatusBadge(experiment.status)}
                </div>

                {experiment.status === 'training' && (
                  <div className="space-y-2 mb-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">
                        Epoch {experiment.metrics.currentEpoch}/{experiment.metrics.totalEpochs}
                      </span>
                    </div>
                    <Progress value={experiment.progress} className="h-2" />
                  </div>
                )}

                <div className="grid grid-cols-4 gap-3 text-sm">
                  <div className="p-2 bg-background rounded text-center">
                    <div className="text-muted-foreground text-xs">Loss</div>
                    <div className="font-mono font-medium">
                      {experiment.metrics.trainLoss.toFixed(4)}
                    </div>
                  </div>
                  {experiment.metrics.accuracy !== undefined && (
                    <div className="p-2 bg-background rounded text-center">
                      <div className="text-muted-foreground text-xs">Accuracy</div>
                      <div className="font-mono font-medium text-green-600">
                        {(experiment.metrics.accuracy * 100).toFixed(1)}%
                      </div>
                    </div>
                  )}
                  {experiment.metrics.mAP !== undefined && (
                    <div className="p-2 bg-background rounded text-center">
                      <div className="text-muted-foreground text-xs">mAP</div>
                      <div className="font-mono font-medium text-green-600">
                        {(experiment.metrics.mAP * 100).toFixed(1)}%
                      </div>
                    </div>
                  )}
                  {experiment.metrics.precision !== undefined && (
                    <div className="p-2 bg-background rounded text-center">
                      <div className="text-muted-foreground text-xs">Precision</div>
                      <div className="font-mono font-medium">
                        {(experiment.metrics.precision * 100).toFixed(1)}%
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>

      {/* Workspace Paths */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FolderOpen className="h-5 w-5" />
            Workspace Paths
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {Object.entries(details.workspacePaths).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between bg-muted/50 p-2 rounded">
              <div>
                <span className="text-sm text-muted-foreground capitalize">{key}:</span>
                <code className="ml-2 text-sm font-mono">{value}</code>
              </div>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={() => handleCopy(value, key)}
              >
                {copiedField === key ? (
                  <Check className="h-3 w-3" />
                ) : (
                  <Copy className="h-3 w-3" />
                )}
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Demo Warning */}
      <Alert className="border-yellow-500/50 bg-yellow-500/5">
        <AlertTriangle className="h-4 w-4 text-yellow-600" />
        <AlertDescription className="text-yellow-700">
          <span className="font-medium">Demo Mode:</span> This is simulated data showing TAO 6.x
          features. In production, training metrics will update in real-time and experiments will
          run on actual GPU hardware.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default TAODetailPanel;
